import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Typography, Spin, message, Button } from "antd";
import { useParams, useHistory } from "react-router-dom";
import { getInvoiceDetails, getClientDetails } from "../../ducks/action";
import { LoadingOutlined, DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo_purevpn from "../../../../../assets/img/logo_purevpn.png";

const { Title, Text } = Typography;
const antIcon = <LoadingOutlined spin />;

const InvoiceDetails = () => {
  const { invoiceId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const invoiceRef = useRef(null);

  const handlePrint = () => {
    const printContent = document.querySelector('.invoice-container').outerHTML;
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);
    
    const printDocument = printFrame.contentWindow.document;
    printDocument.open();
    printDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${invoiceData.invoice_uid || invoiceData.invoiceid}</title>
          <base href="${window.location.origin}/">
          <style>
            @media print {
              @page {
                size: A4;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
                background: #fff;
              }
              .invoice-container {
                width: 794px;
                height: 1123px;
                margin: 0 auto;
                padding: 40px 60px;
                box-sizing: border-box;
                background: #fff;
                box-shadow: none;
              }
              .no-print {
                display: none !important;
              }
              img {
                max-width: 100%;
                height: auto;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.replace(
            /src="\/logo_purevpn.png"/g, 
            `src="${window.location.origin}/logo_purevpn.png"`
          ).replace(
            /src="logo_purevpn.png"/g,
            `src="${window.location.origin}/logo_purevpn.png"`
          )}
        </body>
      </html>
    `);
    printDocument.close();
    
    // Wait for images to load before printing
    const images = printDocument.getElementsByTagName('img');
    let loadedImages = 0;
    
    Array.from(images).forEach(img => {
      img.onload = () => {
        loadedImages++;
        if (loadedImages === images.length) {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          // Remove the iframe after printing
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        }
      };
      img.onerror = () => {
        loadedImages++;
        if (loadedImages === images.length) {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          // Remove the iframe after printing
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        }
      };
    });
    
    // If there are no images, print immediately
    if (images.length === 0) {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      // Remove the iframe after printing
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }
  };

  const handleDownload = async () => {
    try {
      // Create a temporary container for html2canvas
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      document.body.appendChild(tempContainer);

      // Clone the invoice container and update image paths
      const invoiceClone = invoiceRef.current.cloneNode(true);
      const images = invoiceClone.getElementsByTagName('img');
      for (let img of images) {
        if (img.src.includes('logo_purevpn.png')) {
          img.src = `${window.location.origin}/logo_purevpn.png`;
        }
      }
      tempContainer.appendChild(invoiceClone);

      const canvas = await html2canvas(invoiceClone, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [794, 1123]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, 794, 1123);
      
      // Save to Downloads folder
      const fileName = `invoice-${invoiceData.invoice_uid || invoiceData.invoiceid}.pdf`;
      const blob = pdf.output('blob');
      const url = window.URL.createObjectURL(blob);
      
      // Create a download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to download invoice');
    }
  };

  useEffect(() => {
    if (invoiceId) {
      // console.log("Fetching invoice details for ID:", invoiceId);
      // console.log("API URL will be:", `${process.env.REACT_APP_BASE_URL}/api/billing/invoice/${invoiceId}`);
      
      getInvoiceDetails(invoiceId)
        .then((response) => {
          // console.log("Full API Response:", response);
          // console.log("Response data:", response?.data);
          // console.log("Response header:", response?.data?.header);
          // console.log("Response body:", response?.data?.body);
          
          if (response?.data?.header?.code === 200) {
            const invoiceData = response?.data?.body;
            setInvoiceData(invoiceData);
            
            // Fetch client details using userid from invoice data
            if (invoiceData.userid) {
              getClientDetails(invoiceData.userid)
                .then((clientResponse) => {
                  if (clientResponse?.data?.header?.code === 200) {
                    setClientData(clientResponse?.data?.body);
                  } else {
                    // console.log("Error response:", clientResponse?.data?.header?.message);
                    message.error(clientResponse?.data?.header?.message || "Failed to fetch client details");
                  }
                })
                .catch((error) => {
                  console.error("Client Details API Error:", error);
                  message.error("Failed to fetch client details");
                });
            }
          } else {
            // console.log("Error response:", response?.data?.header?.message);
            message.error(response?.data?.header?.message || "Failed to fetch invoice details");
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("API Error:", error);
          console.error("Error response:", error?.response);
          message.error("Something went wrong");
          setLoading(false);
        });
    }
  }, [invoiceId]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin indicator={antIcon} size="large" />
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <Card>
        <Title level={4}>Invoice not found</Title>
        <Text>Invoice ID: {invoiceId}</Text>
        <Text>Please check the console for more details about the API call.</Text>
      </Card>
    );
  }

  // Calculate payments and balance
  const totalAmount = parseFloat(invoiceData.total || 0);
  const balance = parseFloat(invoiceData.balance || 0);
  const payments = totalAmount - balance;

  return (
    <div>
      <div className="no-print" style={{ 
        marginBottom: '20px', 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: '10px'
      }}>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Download
        </Button>
        <Button 
          type="primary" 
          icon={<PrinterOutlined />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </div>
      <div ref={invoiceRef} className="invoice-container" style={{ 
        width: '794px',
        height: '1123px',
        margin: '0 auto', 
        padding: '40px 60px',
        background: '#fff',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        boxSizing: 'border-box'
      }}>
        <div className="invoice-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '40px'
        }}>
          <div className="header-left" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            <img 
              src={logo_purevpn} 
              alt="PureVPN" 
              style={{ height: '55px', marginRight: '25px' }} 
            />
            <div className="company-details" style={{ fontSize: '11px', lineHeight: '1.5' }}>
              <p style={{ marginTop: 0, marginBottom: '0.5em' }}><strong>GZ Systems Ltd</strong></p>
              <p style={{ marginTop: 0, marginBottom: '0.5em' }}>Intershore Chambers, P.O Box 4342, RoadTown, Tortola,</p>
              <p style={{ marginTop: 0, marginBottom: '0.5em' }}>VG1110VG</p>
            </div>
          </div>
          <div className="invoice-section" style={{ textAlign: 'left' }}>
            <h2 style={{ 
              margin: '0 0 10px', 
              color: '#1e2d3d', 
              fontSize: '20px'
            }}>Invoice</h2>
            <div className="invoice-details-grid" style={{ 
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              rowGap: '4px',
              columnGap: '20px',
              fontSize: '11px',
              margin: '20px 0'
            }}>
              <div className="label-d" style={{ minWidth: '85px' }}>Invoice Number:</div>
              <div className="value" style={{ textAlign: 'right' }}>#{invoiceData.invoice_uid || invoiceData.invoiceid}</div>
              <div className="label-d" style={{ minWidth: '85px' }}>Issue Date:</div>
              <div className="value" style={{ textAlign: 'right' }}>{new Date(invoiceData.date || invoiceData.created_at).toLocaleDateString()}</div>
              <div className="label-d" style={{ minWidth: '85px' }}>Due Date:</div>
              <div className="value" style={{ textAlign: 'right' }}>{new Date(invoiceData.duedate).toLocaleDateString()}</div>
            </div>
            {(invoiceData.status === "Unpaid" || invoiceData.status === "Draft") && (
              <div style={{ display: 'flex', marginTop: '10px' }}>
                <div style={{ 
                  backgroundColor: '#1e2d3d',
                  color: 'white',
                  padding: '8px 0',
                  borderRadius: '4px 0 0 4px',
                  fontSize: '12px',
                  minWidth: '85px',
                  textAlign: 'center',
                  fontWeight: '700'
                }}>OPEN</div>
                <div style={{ 
                  backgroundColor: '#f2f2f2',
                  padding: '8px 5px 8px 20px',
                  borderRadius: '0 4px 4px 0',
                  borderLeft: '1px solid #ccc',
                  width: '125px',
                  textAlign: 'right',
                  fontSize: '12px'
                }}>${balance.toFixed(2)}</div>
              </div>
            )}
          </div>
        </div>

        <div className="bill-to" style={{ marginBottom: '28px', fontSize: '11px', marginTop: '-10px', lineHeight: 1.5 }}>
          <p style={{ marginTop: 0, marginBottom: '0.5em' }}><strong>Bill To:</strong></p>
          <p className="partner" style={{ marginTop: 0, marginBottom: '0.5em' }}>{clientData?.companyname}</p>
          <p className="partner-name" style={{ marginTop: 0, marginBottom: '0.5em' }}>{clientData?.firstname} {clientData?.lastname}</p>
          <p style={{ marginTop: 0, marginBottom: '0.5em' }}>{clientData?.address1}{clientData?.address1 && clientData?.address2 ? ', ' : ''}{clientData?.address2}</p>
          <p style={{ marginTop: 0, marginBottom: '0.5em' }}>{clientData?.city}{clientData?.city && clientData?.state ? ', ' : ''}{clientData?.state}{clientData?.postcode ? ` ${clientData.postcode}` : ''}</p>
          {/* <p style={{ marginTop: 0, marginBottom: '0.5em' }}>{clientData?.country}</p> */}
        </div>

        <div className="invoice-items" style={{ marginTop: '40px' }}>
          <table style={{ 
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px'
          }}>
            <thead>
              <tr>
                <th style={{ padding: '15px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Item</th>
                <th style={{ padding: '15px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>Quantity</th>
                <th style={{ padding: '15px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>Unit Price</th>
                <th style={{ padding: '15px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.items?.item?.map((item, index) => {
                try {
                  // Parse the description string
                  const [billingType = '', billingMonth = '', userType = '', rateInfo = ''] = item?.description?.split('|') || [];
                  // Extract quantity and rate from rateInfo
                  const rateMatch = rateInfo?.match(/Rate: ([\d.]+) × ([\d.]+)\s*(Account|users|GB)?/);
                  const quantity = rateMatch ? rateMatch[2] : '1';
                  const quantityLabel = rateMatch && rateMatch[3] ? rateMatch[3] : '';
                  const rate = rateMatch ? rateMatch[1] : item?.amount || '0';
                  // Parse month for date range
                  let startDateStr = '', endDateStr = '';
                  if (billingMonth) {
                    // Try to parse e.g. 'Mar 2025' or 'February 2025'
                    const dateObj = new Date(billingMonth + ' 01');
                    if (!isNaN(dateObj)) {
                      const year = dateObj.getFullYear();
                      const month = dateObj.getMonth();
                      const start = new Date(year, month, 1);
                      const end = new Date(year, month + 1, 0);
                      const pad = n => n.toString().padStart(2, '0');
                      startDateStr = `${pad(start.getMonth() + 1)}/${pad(start.getDate())}/${start.getFullYear().toString().slice(-2)}`;
                      endDateStr = `${pad(end.getMonth() + 1)}/${pad(end.getDate())}/${end.getFullYear().toString().slice(-2)}`;
                    }
                  }
                  return (
                    <React.Fragment key={index}>
                      <tr>
                        <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #ddd', textAlign: 'left', lineHeight: 1.3 }}>
                          <div style={{ fontWeight: 500, color: '#222' }}>
                            {billingType && userType ? `${billingType} (${userType})` : item?.description || 'Item'}{billingMonth ? ` - ${billingMonth}` : ''}
                            {startDateStr && endDateStr && (
                              <div style={{ fontSize: '10px', color: '#222', marginTop: '2px', marginBottom: '2px' }}>{startDateStr} - {endDateStr}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #ddd', textAlign: 'center', lineHeight: 1.3 }}>
                          {quantity}{quantityLabel ? ` ${quantityLabel}` : ''}
                        </td>
                        <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #ddd', textAlign: 'center', lineHeight: 1.3 }}>${rate} USD</td>
                        <td style={{ padding: '7px 10px 7px 0', borderBottom: '1px solid #ddd', textAlign: 'center', lineHeight: 1.3 }}>${item?.amount || '0.00'} USD</td>
                      </tr>
                    </React.Fragment>
                  );
                } catch (error) {
                  console.error('Error parsing invoice item:', error);
                  return (
                    <tr key={index}>
                      <td style={{ padding: '10px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                        {item?.description || 'Item'}
                      </td>
                      <td style={{ padding: '10px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>1</td>
                      <td style={{ padding: '10px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>${item?.amount || '0.00'}</td>
                      <td style={{ padding: '10px 10px 10px 0', borderBottom: '1px solid #ddd', textAlign: 'center' }}>${item?.amount || '0.00'}</td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>

        {invoiceData.notes && (
          <div className="stripe-totals-row" style={{ margin: '18px 0', fontSize: '11px' }}>
            <p className="stripe-link"><b>
              Stripe Payment Link: <a href={decodeURIComponent(invoiceData.notes)} style={{ color: '#1a1a1a', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{invoiceData.notes}</a>
            </b>
            </p>
          </div>
        )}

        <div className="invoice-footer" style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '22px'
        }}>
          <div className="footer" style={{ 
            fontSize: '12px',
            color: '#222',
            lineHeight: '1.6',
            width: '55%'
          }}>
            <p><strong>Bank Account Details:</strong><br />
            {clientData?.customfields2 ? (
              clientData.customfields2.split('\r\n').map((line, index) => {
                // Create a temporary element to decode HTML entities
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = line;
                const decodedText = tempDiv.textContent || tempDiv.innerText;
                
                return (
                  <span key={index}>
                    {decodedText}
                    {index < clientData.customfields2.split('\r\n').length - 1 && <br />}
                  </span>
                );
              })
            ) : (
              <>
                Settlement Currency: USD<br />
                Beneficiary Bank Name: Citibank N.A.<br />
                Bank Address & Country: 21/F, Citi Tower, One Bay East, 83 Hoi Bun Road, Kwun Tong, Kowloon, Hong Kong.<br />
                Name of Account Holder: GZ Systems Limited<br />
                Account Number: 1101904012<br />
                Base Number: 101904<br />
                Bank Code: 006<br />
                Branch Code: 391<br />
                SWIFT Code: CITIHKHX
              </>
            )}</p>
            <p>If you have any questions about this invoice, please contact <a href="mailto:bkhan@purevpn.com" style={{ color: '#1a1a1a', textDecoration: 'underline' }}>bkhan@purevpn.com</a></p>
          </div>

          <table className="totals" style={{ 
            width: '35%',
            fontSize: '11px',
            height: '120px'
          }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px 0', textAlign: 'left' }}><strong>Total</strong></td>
                <td style={{ padding: '8px 0', textAlign: 'right' }}>${totalAmount.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={2}><div style={{ borderBottom: '1px solid #ddd', margin: '0 0 4px 0' }} /></td>
              </tr>
              <tr>
                <td style={{ padding: '8px 0', textAlign: 'left' }}>Payments</td>
                <td style={{ padding: '8px 0', textAlign: 'right' }}>${payments.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={2}><div style={{ borderBottom: '1px solid #ddd', margin: '0 0 4px 0' }} /></td>
              </tr>
              <tr>
                <td style={{ padding: '8px 0', textAlign: 'left' }}><strong>DUE</strong></td>
                <td style={{ padding: '8px 0', textAlign: 'right' }}><strong>${balance.toFixed(2)}</strong></td>
              </tr>
              <tr>
                <td colSpan={2}><div style={{ borderBottom: '1px solid #ddd', margin: '0 0 4px 0' }} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetails; 